
import { LeadFieldConfig, LeadReportHeaderField } from "src/types/experiences/leadsAndReports";

export const ALWAYS_VISIBLE_UID = "firstName";

/**
 * Appends lead custom fields (those with `uid == null`) discovered in the API
 * response onto the static base header, matching legacy leadAnalytics behaviour.
 * A custom field is keyed by its `field` id and only added once.
 */
export const mergeCustomFields = (
  base: LeadReportHeaderField[],
  leads: any[],
): LeadReportHeaderField[] => {
  const merged = [...base];
  (leads || []).forEach((lead) => {
    (lead?.fields || []).forEach((field: any) => {
      if (!field || field.uid != null) return;
      const exists = merged.some((h) => h.field === field.field);
      if (!exists) {
        merged.push({
          name: field.name,
          field: field.field,
          values: field.values,
          uid: field.uid,
        });
      }
    });
  });
  return merged;
};

export const buildDefaultFieldConfig = (header: LeadReportHeaderField[]): LeadFieldConfig[] =>
  header.map((h) => ({
    fieldId: h.field,
    uid: h.uid,
    name: h.name,
    visible: true,
  }));

export const loadFieldConfig = (
  storyboardId: string,
  header: LeadReportHeaderField[],
): LeadFieldConfig[] => {
  const defaults = buildDefaultFieldConfig(header);
  let saved: LeadFieldConfig[] | null = null;
  try {
    const raw = localStorage.getItem(storyboardId);
    saved = raw ? (JSON.parse(raw) as LeadFieldConfig[]) : null;
  } catch {
    saved = null;
  }
  if (!saved || saved.length === 0) return defaults;

  const defaultsByField = new Map(defaults.map((d) => [d.fieldId, d]));

  const reconciled: LeadFieldConfig[] = [];
  const usedFields = new Set<string>();
  for (const s of saved) {
    const def = defaultsByField.get(s.fieldId);
    if (!def) continue;
    usedFields.add(def.fieldId);
    const visible = def.uid === ALWAYS_VISIBLE_UID ? true : s.visible;
    reconciled.push({ ...def, visible });
  }

  for (const def of defaults) {
    if (usedFields.has(def.fieldId)) continue;
    reconciled.push({ ...def });
  }

  return reconciled;
};

/** Persist the field config for a storyboard. */
export const saveFieldConfig = (storyboardId: string, fields: LeadFieldConfig[]): void => {
  try {
    localStorage.setItem(storyboardId, JSON.stringify(fields));
  } catch {
    /* ignore quota / serialization errors */
  }
};
